import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface PdfExportOptions {
  filename?: string;
  orientation?: 'p' | 'l';               // portrait / landscape
  unit?: 'mm' | 'pt' | 'cm' | 'in';
  format?: 'a4' | 'letter' | [number, number];
  scale?: number;                        // html2canvas scaling (crispness)
  marginMm?: number;                     // page margins (mm)
  ignoreSelectors?: string[];            // selectors to hide during capture
}

@Injectable({ providedIn: 'root' })
export class PdfExportService {
  async downloadElement(el: HTMLElement, opts: PdfExportOptions = {}) {
    if (!el) throw new Error('PdfExportService: element is required');

    const {
      filename = 'Credentialing-Preview.pdf',
      orientation = 'p',
      unit = 'mm',
      format = 'a4',
      scale = Math.max(2, window.devicePixelRatio || 2),
      marginMm = 8,
      ignoreSelectors = []
    } = opts;

    // Hide elements you don’t want in PDF (e.g., action bar)
    const hidden: Array<{ node: HTMLElement; prev: string }> = [];
    try {
      ignoreSelectors.forEach(sel =>
        el.querySelectorAll<HTMLElement>(sel).forEach(node => {
          hidden.push({ node, prev: node.style.display });
          node.style.display = 'none';
        })
      );
    } catch {}

    const canvas = await html2canvas(el, {
      scale,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false
    });

    // restore hidden nodes
    hidden.forEach(h => (h.node.style.display = h.prev));

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation, unit, format });

    // page sizes
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    // px -> mm helper
    const pxToMm = (px: number) => px * 0.264583;

    // content box inside margins
    const contentW = pageW - marginMm * 2;
    const contentH = pageH - marginMm * 2;

    // image size (mm) at 1:1
    const imgWmm = pxToMm(canvas.width);
    const imgHmm = pxToMm(canvas.height);

    // scale to fit width
    const ratio = contentW / imgWmm;
    const renderW = contentW;
    const renderH = imgHmm * ratio;

    if (renderH <= contentH) {
      pdf.addImage(imgData, 'PNG', marginMm, marginMm, renderW, renderH, undefined, 'FAST');
    } else {
      // multi-page: slice the tall canvas into page chunks
      const pageCanvas = document.createElement('canvas');
      const ctx = pageCanvas.getContext('2d')!;
      const pageHeightPx = Math.floor(contentH / 0.264583 / ratio);

      pageCanvas.width = canvas.width;
      pageCanvas.height = pageHeightPx;

      let y = 0;
      let page = 0;

      while (y < canvas.height) {
        const h = Math.min(pageHeightPx, canvas.height - y);
        ctx.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, pageCanvas.width, h);
        const slice = pageCanvas.toDataURL('image/png');

        if (page > 0) pdf.addPage();
        const sliceHmm = pxToMm(h) * ratio;

        pdf.addImage(slice, 'PNG', marginMm, marginMm, renderW, sliceHmm, undefined, 'FAST');
        y += pageHeightPx;
        page++;
      }
    }
    pdf.save(filename);
  }
  async getPdfBlob(el: HTMLElement, opts: PdfExportOptions = {}): Promise<Blob> {

  if (!el) throw new Error('PdfExportService: element is required');
  const {
    orientation = 'p',
    unit = 'mm',
    format = 'a4',
    scale = Math.max(2, window.devicePixelRatio || 2),
    marginMm = 8,
    ignoreSelectors = []
  } = opts;
 
  const hidden: Array<{ node: HTMLElement; prev: string }> = [];

  try {
    ignoreSelectors.forEach(sel =>
      el.querySelectorAll<HTMLElement>(sel).forEach(node => {
        hidden.push({ node, prev: node.style.display });
        node.style.display = 'none';
      })
    );

  } catch {}
 
  const canvas = await html2canvas(el, {
    scale,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
 
  hidden.forEach(h => (h.node.style.display = h.prev));
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({ orientation, unit, format });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const pxToMm = (px: number) => px * 0.264583;
  const contentW = pageW - marginMm * 2;
  const contentH = pageH - marginMm * 2;
  const imgWmm = pxToMm(canvas.width);
  const imgHmm = pxToMm(canvas.height);
  const ratio = contentW / imgWmm;
  const renderW = contentW;
  const renderH = imgHmm * ratio;

  const now = new Date();
  const submittedDate = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
 
    pdf.setFontSize(10);
 
    pdf.text(
      `Submitted Date - ${submittedDate}`,
      marginMm,
      marginMm
    );
  const topOffset = marginMm + 8;

  if (renderH <= contentH) {
    pdf.addImage(imgData, 'PNG', marginMm, topOffset, renderW, renderH, undefined, 'FAST');
  } else {
    const pageCanvas = document.createElement('canvas');
    const ctx = pageCanvas.getContext('2d')!;
    const pageHeightPx = Math.floor(contentH / 0.264583 / ratio);
    pageCanvas.width = canvas.width;
    pageCanvas.height = pageHeightPx;
    let y = 0;
    let page = 0;
    while (y < canvas.height) {
      const h = Math.min(pageHeightPx, canvas.height - y);
      ctx.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, pageCanvas.width, h);
      const slice = pageCanvas.toDataURL('image/png');
      if (page > 0) pdf.addPage();
      const sliceHmm = pxToMm(h) * ratio;
      pdf.addImage(slice, 'PNG', marginMm, topOffset, renderW, sliceHmm, undefined, 'FAST');
      y += pageHeightPx;
      page++;
    }
  }
    return pdf.output('blob');
 }
 
}